import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget.
 * Loads the CF script once and renders an explicit widget that calls
 * `onVerify(token)` on success. Exposes a `reset()` method through ref.
 *
 * Props :
 *  - siteKey      : Turnstile site key (public)
 *  - onVerify     : (token) => void — token to send to backend
 *  - onExpire     : () => void
 *  - onError      : (err) => void
 *  - theme        : 'light' | 'dark' | 'auto'  (default 'light')
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstile_ready&render=explicit";
let _scriptPromise = null;

const loadScript = () => {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve) => {
    if (window.turnstile) return resolve();
    window.__turnstile_ready = () => resolve();
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  });
  return _scriptPromise;
};

export const Turnstile = ({ siteKey, onVerify, onExpire, onError, theme = "light" }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        callback: (token) => onVerify?.(token),
        "expired-callback": () => onExpire?.(),
        "error-callback": (err) => onError?.(err),
      });
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" />;
};

export default Turnstile;
